import { useNavigate } from 'react-router-dom';
import { Plus, Radio } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import { Button } from '@vnetwork/ui/primitives';
import { useServiceDetail } from '@/api/media/service-detail';
import { useStatusSummary } from '@/api/media/live-live-streams';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { StatusStatCards } from './StatusStatCards';
import { OverviewCharts } from './TranscodingChartCard';
import { RecentJobsCard } from './RecentJobsCard';
import styles from './DashboardPage.module.css';

interface Props {
  serviceId: string;
}

/**
 * Live Transcoding dashboard — service identity/status, job-status summary,
 * overview duration charts, and a recent-jobs snapshot with a link to the full
 * current-jobs list. Owns the console's root page chrome (Container + PageHeader).
 */
export function LiveDashboard({ serviceId }: Props) {
  const navigate = useNavigate();
  const base = `/services/live/${serviceId}`;

  const service = useServiceDetail(serviceId);
  const serviceStatus = (service.data?.status ?? '').toLowerCase();

  const { data: summary, isPending: summaryPending } = useStatusSummary(
    serviceId,
    { refetchInterval: 10_000, refetchIntervalInBackground: false },
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs />}
        icon={<Radio size={22} />}
        title="Live Transcoding"
        description={service.data?.service_name || 'Live event transcoding with presets, recording and DRM protection.'}
        actions={
          <StatusPill
            tone={serviceStatus === 'active' ? 'success' : service.isLoading ? 'muted' : 'warning'}
            loading={service.isLoading}
          >
            {service.isLoading ? 'Loading' : service.data?.status || 'Unknown'}
          </StatusPill>
        }
      />
      <div className={styles.stack}>
      {/* Job status summary */}
      <Section
        title="Job Status Summary"
        description="Current snapshot of all live stream jobs."
        style={{ marginBottom: 0 }}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={15} />}
              onClick={() => navigate(`${base}/linear-jobs/create`)}
            >
              Create Linear Job
            </Button>
            <Button
              size="sm"
              startIcon={<Plus size={15} />}
              onClick={() => navigate(`${base}/event-jobs/create`)}
            >
              Create Event Job
            </Button>
          </div>
        }
      >
        <StatusStatCards summary={summary} isLoading={summaryPending} />
      </Section>

      {/* Overview duration charts */}
      <OverviewCharts serviceId={serviceId} />

      {/* Recent jobs list */}
      <RecentJobsCard serviceId={serviceId} />
      </div>
    </Container>
  );
}

export default LiveDashboard;
